// Stalled-replay ledger (#908) — the entities whose maintainer run died on a
// credential problem and should be re-driven once the account's BYOK key is
// fixed. Recorded by the agent.run consumer at failure time (it holds the full
// `AgentRunPayload` + the resolved account), drained by the worker when a key
// transitions `unverified|invalid → valid`.
//
// Pure domain: the cause allow-list + the drain cap. The row shape + SQL live
// in `@developerz/db`; the record/drain wiring in agents-manager + worker.

/**
 * Failure causes a BYOK key fix (or a provider recharge) can plausibly resolve,
 * so the entity is worth re-driving on the next `→ valid` transition:
 *
 * - `provider_failure`  — the provider returned an error (auth, non-JSON body,
 *   5xx) — the wurk case (`Insufficient balance`, then a bad model/endpoint).
 * - `credits_exhausted` — the key's provider wallet ran dry.
 * - `byok_invalid`      — the key itself was rejected (401/403).
 *
 * A genuine model *refusal* is NOT one of these (it completes with a refusal, or
 * escalates a guardrail) — so it is never recorded and never auto-replayed.
 */
export const CREDENTIAL_RESOLVABLE_CAUSES = [
  'provider_failure',
  'credits_exhausted',
  'byok_invalid',
] as const;

const CREDENTIAL_RESOLVABLE_SET: ReadonlySet<string> = new Set(CREDENTIAL_RESOLVABLE_CAUSES);

/**
 * Should a session that failed with `cause` be recorded for replay-on-key-fix?
 * True only for the credential-resolvable causes above — a run that failed for
 * any other reason (a real bug, a guardrail, a genuine refusal) is left alone,
 * so fixing a key never re-runs work that a key never broke.
 */
export function isCredentialResolvableCause(cause: string | null | undefined): boolean {
  return cause != null && CREDENTIAL_RESOLVABLE_SET.has(cause);
}

/**
 * Cause recorded when an entity was REFUSED (not failed) because the account's
 * billing was paused (#919) — an account-level stop cleared by `invoice.paid`,
 * not a per-key one. Recorded at the pre-session billing gate, drained when the
 * account resumes. Not in `CREDENTIAL_RESOLVABLE_CAUSES`: it is not a credential
 * fault and a key-fix does not clear it (a billing-resume does).
 */
export const BILLING_PAUSED_CAUSE = 'billing_paused' as const;

/**
 * Cause recorded when an entity was REFUSED because the account had no usable BYOK
 * key (#919) — the other replayable pre-session gate. Drained when a key transition
 * fires the resume signal (adding + verifying a key → `unverified → valid`), so
 * issues that arrived before a key existed re-drive once one does. Also the reason
 * a re-driven `billing_paused` entity that then hits the BYOK gate is re-recorded
 * rather than lost: any still-unresolved pre-session refusal keeps a durable row.
 */
export const NO_BYOK_CAUSE = 'no_byok' as const;

/**
 * Hard ceiling on entities re-driven per key-verify drain (#908, requirement 3).
 * Bounds the blast radius of a single fix AND a repeatedly-flapping key: each
 * `→ valid` transition drains at most this many, overflow is left in the ledger
 * for the next drain and logged (never silently dropped). Coalescing dedups a
 * replay storm to one run per entity on top of this.
 */
export const STALLED_REPLAY_DRAIN_CAP = 100;

/**
 * Retention for a dead lease on a `stalled_replay` row (#908). A lease older
 * than this belongs to a drive that will never complete (its worker died
 * without `completeStalledReplay`), so the row is dead weight, deleted INSIDE
 * the drain — before the claim, by `pruneStalledReplayLeases` — so the single
 * drain seam enforces retention with no caller change.
 *
 * ASYMMETRY with unleased rows: a row with NULL `claimed_until` is a fresh
 * failure awaiting its first drain, and per #4173 an unreplayable UNLEASED row
 * is kept in place as the customer's only evidence (counted by
 * `countUnreplayableStalledReplay`, never deleted). The predicate is strictly
 * `claimed_until < now() - N days`, so a NULL lease never matches.
 */
export const STALLED_REPLAY_LEASE_RETENTION_DAYS = 7;
